/* Glance recap card. Every `var(--*)` must resolve against the shipped token sheet (scripts/check-token-refs.ts). */

.card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-12);
  padding: var(--spacing-16);
  border: 1px solid var(--stroke-neutral);
  border-radius: var(--radius-8);
  /* SOLID, opaque fill — masks the center of the ::after / ::before
   * gradients so the AI glow only reads as a thin border ring + soft halo,
   * never bleeding through the card body. */
  background: var(--bg-secondary);
}

/* Static AI-glow border ring. Sits one layer behind the card's solid background; the 1.5px outset
 * makes it peek out as a hairline ring. Present in EVERY state — a quiet "AI-authored" marker. */
.card::after {
  content: "";
  position: absolute;
  inset: -1.5px;
  border-radius: inherit;
  z-index: -1;
  background: var(--ai-glow-static);
}

/* Inner content column AND the focusable landmark (an implicit region — <section> + aria-label, no explicit role). The card's visual chrome —
 * border, glow ring, padding, background — stays on `.card`; this wrapper owns the flex layout and
 * receives programmatic focus when the card surfaces. tabIndex=-1 keeps it out of the keyboard
 * tab order; suppress the ring via `:focus-visible` (matches the package convention — programmatic focus doesn't trigger it anyway). */
.region {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-12);
}

.region:focus-visible {
  outline: none;
}

.body {
  margin: 0;
  font: var(--text-body-regular);
  color: var(--text-secondary);
}

/* ----- Card header (notification-style: brand icon + app name + AI tag · close) ----- */

/* Mirrors the Notification primitive's `.headerRow`: a space-between flex with the meta cluster
 * on the left and the close IconButton on the right. */
.cardHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-8);
}

.headerMeta {
  display: flex;
  align-items: center;
  gap: var(--spacing-8);
  min-width: 0;
}

.brandIcon {
  color: var(--brand);
  flex: 0 0 auto;
}

/* App name shares the Notification primitive's Micro/Medium type + secondary tone. */
.appName {
  font: var(--text-micro-medium);
  letter-spacing: var(--text-xs-tracking);
  color: var(--text-secondary);
}

/* Title row below the header — same body-semibold type the Notification `.title` uses. Doubles as
 * the AI headline once one streams (the headline IS this title, not a separate paragraph). */
.glanceTitle {
  margin: 0;
  font: var(--text-body-semibold);
  color: var(--text-primary);
}

/* Light "AI summary" attribution chip — voice, not source impersonation. */
.aiTag {
  flex: 0 0 auto;
  font: var(--text-micro-semibold);
  color: var(--text-on-dark);
  background: var(--brand);
  border-radius: var(--radius-round);
  padding: 0 var(--spacing-8);
}

.caughtUp {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-8);
  padding: var(--spacing-16) 0;
  text-align: center;
}

.caughtUpIcon {
  color: var(--text-tertiary);
}

.caughtUpText {
  color: var(--text-secondary);
  font: var(--text-body-regular);
  margin: 0;
}

/* ----- AI narration (read-only) ----- */

.narration {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-8);
}

/* ----- Needs-you section ----- */

.section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-8);
}

.sectionLabel {
  margin: 0;
  font: var(--text-micro-semibold);
  letter-spacing: var(--text-xs-tracking);
  color: var(--text-tertiary);
  text-transform: uppercase;
}

.rows {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-8);
  margin: 0;
  padding: 0;
  list-style: none;
}

.row {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-8);
}

.rowIcon {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: var(--radius-4);
  flex: 0 0 auto;
}

.rowGlyph {
  color: var(--text-tertiary);
  flex: 0 0 auto;
}

.rowBody {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1 1 auto;
}

.rowLabel {
  font: var(--text-micro-medium);
  color: var(--text-secondary);
}

.rowText {
  font: var(--text-body-regular);
  color: var(--text-primary);
}

/* ----- Download onboarding (downloadable) ----- */

.onboarding {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-8);
  align-items: flex-start;
}

.privacyNote {
  margin: 0;
  font: var(--text-micro-regular);
  color: var(--text-tertiary);
}

/* ----- Downloading progress ----- */

.downloadingState {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-8);
}

.progress {
  width: 100%;
  height: var(--spacing-8);
  appearance: none;
}

.progress::-webkit-progress-bar {
  background: var(--bg-primary);
  border-radius: var(--radius-4);
}

.progress::-webkit-progress-value {
  background: var(--brand);
  border-radius: var(--radius-4);
}

/* visible numeric percentage beneath the bar so progress magnitude isn't conveyed by fill alone. */
.progressPercent {
  font: var(--text-micro-regular);
  color: var(--text-tertiary);
}

/* ----- Error ----- */

/* Error column — the reason-mapped note above a left-aligned Retry button (the card is a flex
 * column whose default align-items would otherwise stretch a bare Button edge-to-edge). */
.errorState {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-8);
  align-items: flex-start;
}

.errorNote {
  margin: 0;
  font: var(--text-body-regular);
  /* reads as an error (negative tone), not generic gray body text. */
  color: var(--text-negative);
}

/* Reduced-motion is the DEFAULT: every animation is gated behind `prefers-reduced-motion: no-preference`,
 * so reduced-motion (or an engine without @property) gets the static glow/skeleton/caret — no `reduce` block needed. */

/* ----- DigestCard (brief + needs-you + quiet tally) ----- */

.digest {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-12);
}

.brief {
  margin: 0;
  font: var(--text-body-regular);
  color: var(--text-primary);
}

.scanning {
  margin: 0;
  font: var(--text-micro-regular);
  color: var(--text-tertiary);
}

.affordance {
  flex: none;
  font: var(--text-micro-semibold);
  color: var(--brand);
}

.quiet {
  margin: 0;
  font: var(--text-micro-regular);
  color: var(--text-tertiary);
}
